import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Flag, Layers, Lightbulb, Play, RefreshCw, Target } from 'lucide-react';
import { Button, Modal } from '../ui';
import { aiErrorInfo, aiService } from '../../services/api';
import { AIState, stateOfError } from './AIStates';
import { useAIStatus } from './useAIStatus';
import './AIQuestions.css';

const DIFFICULTY = { easy: 'Fácil', medium: 'Média', hard: 'Difícil' };
const letter = (i) => String.fromCharCode(65 + i);

/**
 * Questões geradas que dá para usar (2c): cada uma vira card com alternativas e explicação, e vai para
 * o próximo simulado, para os flashcards ou para um simulado agora.
 */
export function AIQuestions({ topicId, topicName, courseSlug, open, onClose }) {
  const navigate = useNavigate();
  const [status] = useAIStatus(open);
  const [questions, setQuestions] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const started = useRef(false);

  const generate = useCallback(async (avoid) => {
    setBusy('generate');
    setError(null);
    try {
      const r = await aiService.questions({ topicId, count: 5, avoid });
      setQuestions(r.questions);
      setOpenIdx(0);
      setAnswers({});
      setMarks({});
    } catch (err) {
      setError(aiErrorInfo(err));
    } finally {
      setBusy(null);
    }
  }, [topicId]);

  useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    generate();
  }, [open, generate]);

  const queue = async (i) => {
    setBusy(`queue-${i}`);
    try {
      await aiService.queueQuestions(topicId, [questions[i]]);
      setMarks(m => ({ ...m, [i]: { ...m[i], queued: true } }));
    } catch (err) {
      setError(aiErrorInfo(err));
    } finally {
      setBusy(null);
    }
  };

  const toCard = async (i) => {
    const q = questions[i];
    const right = q.correct.map(l => q.options[l.charCodeAt(0) - 65]).join(' / ');
    try {
      await aiService.saveFlashcards({ topicId, source: 'questions', cards: [{ front: q.statement, back: `${right}. ${q.explanation}` }] });
      setMarks(m => ({ ...m, [i]: { ...m[i], card: true } }));
    } catch (err) {
      setError(aiErrorInfo(err));
    }
  };

  const flag = (i) => {
    setQuestions(list => list.filter((_, j) => j !== i));
    setOpenIdx(0);
  };

  const examNow = async () => {
    setBusy('exam');
    try {
      const r = await aiService.examNow(topicId, questions);
      navigate(`/curso/${courseSlug}/exam/${r.examId}`);
    } catch (err) {
      setError(aiErrorInfo(err));
      setBusy(null);
    }
  };

  const queued = Object.values(marks).filter(m => m.queued).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={760}
      title={questions ? `${questions.length} questões do tópico ${topicName}` : `Questões do tópico ${topicName}`}
      footer={questions && questions.length > 0 && (
        <div className="aiq-foot">
          <span>{queued} de {questions.length} {queued === 1 ? 'adicionada' : 'adicionadas'} ao próximo simulado</span>
          <Button variant="outline" icon={RefreshCw} onClick={() => generate(questions.map(q => q.statement))} disabled={!!busy}>
            {busy === 'generate' ? 'Gerando...' : 'Gerar outras'}
          </Button>
          <Button variant="accent" icon={Play} onClick={examNow} disabled={!!busy || !courseSlug}>
            Responder as {questions.length} agora
          </Button>
        </div>
      )}
    >
      <p className="aiq-sub">Geradas do material do tópico · estilo certificação</p>
      {error && <AIState kind={stateOfError(error.code)} status={status} message={error.message} onRetry={() => generate()} compact />}
      {!questions && !error && (
        <div className="ai-thinking"><span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>Montando as questões...</div>
      )}
      {questions && (
        <ol className="aiq-list">
          {questions.map((q, i) => {
            const isOpen = openIdx === i;
            const picked = answers[i];
            const revealed = picked != null;
            return (
              <li key={q.statement} className={`aiq ${isOpen ? 'is-open' : ''}`}>
                {!isOpen ? (
                  <button type="button" className="aiq__row" onClick={() => setOpenIdx(i)}>
                    <span className="aiq__n">{String(i + 1).padStart(2, '0')}</span>
                    <span className="aiq__statement">{q.statement}</span>
                    {marks[i]?.queued && <span className="aiq__flag"><Check size={12} aria-hidden="true" />no simulado</span>}
                    <ChevronDown size={15} aria-hidden="true" />
                  </button>
                ) : (
                  <div className="aiq__card">
                    <div className="aiq__meta">
                      <span>Questão {i + 1}</span>
                      <span className="aiq__diff">{DIFFICULTY[q.difficulty] || 'Média'}</span>
                      <span className="aiq__of">{i + 1} de {questions.length}</span>
                    </div>
                    <h3>{q.statement}</h3>
                    <ul className="aiq__opts">
                      {q.options.map((o, k) => {
                        const l = letter(k);
                        const correct = q.correct.includes(l);
                        const cls = revealed ? (correct ? 'is-correct' : picked === l ? 'is-wrong' : '') : '';
                        return (
                          <li key={k}>
                            <button type="button" className={`aiq__opt ${cls}`} onClick={() => !revealed && setAnswers(a => ({ ...a, [i]: l }))} disabled={revealed}>
                              <b>{l}</b><span>{o}</span>{revealed && correct && <Check size={16} aria-hidden="true" />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {revealed && <p className="aiq__why"><Lightbulb size={15} aria-hidden="true" />{q.explanation}</p>}
                    <div className="aiq__actions">
                      <Button size="sm" icon={marks[i]?.queued ? Check : Target} onClick={() => queue(i)} disabled={!!busy || marks[i]?.queued}>
                        {marks[i]?.queued ? 'No próximo simulado' : 'Pôr no próximo simulado'}
                      </Button>
                      <Button size="sm" variant="outline" icon={marks[i]?.card ? Check : Layers} onClick={() => toCard(i)} disabled={marks[i]?.card}>
                        {marks[i]?.card ? 'Nos flashcards' : 'Virar flashcard'}
                      </Button>
                      <button type="button" className="aiq__report ss-iconbtn" onClick={() => flag(i)} title="Questão com problema: tirar da lista" aria-label="Questão com problema">
                        <Flag size={15} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Modal>
  );
}
